import {
  Modal,
  Box,
  Text,
  Group,
  Grid,
  Select,
  TextInput,
  Button,
  NumberInput,
  MultiSelect,
  Checkbox,
  Paper,
  ScrollArea,
} from "@mantine/core";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconCalendarTime, IconChecklist, IconCircleCheckFilled, IconClockHour1, IconDeviceFloppy, IconFileText, IconMessageExclamation, IconPencilCheck, IconSearch, IconSettingsUp, IconUserCheck, IconUserCog } from "@tabler/icons-react";
import { crearEstadoInicialTarea, type IModalTareaVistaProps } from "../interfaces";
import { DateTimePicker } from "@mantine/dates";
import { MapaVista } from "../../../globales/componentes/mapa/mapaVista";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { obtenerZonaHorariaPorPais, obtenerNombrePaisPorId } from "../utils/zonaHorariaUtils";
import { EnumTipoTarea } from "../enums/enumTipoTarea";
import { EnumEstadoTarea } from "../enums/enumEstadoTarea";

dayjs.extend(utc);
dayjs.extend(timezone);


// Alto de la lista de secciones del QD (filas de ~31px): fijo en celular, entre mínimo y máximo en computador
const ALTO_LISTA_SECCIONES_MOVIL = 186;
const ALTO_LISTA_SECCIONES_MIN = 94;
const ALTO_LISTA_SECCIONES_MAX = 280;
// Breakpoint md de Mantine (62em)
const ANCHO_MINIMO_ESCRITORIO = 992;

const ModalTareaVista: React.FunctionComponent<IModalTareaVistaProps> = ({
  abrir,
  guardarEditarTarea,
  manejarCambioValorTarea,
  abrirModalTarea,
  entidadesRegistro,
  agregarError,
  tareaSeleccionado,
  errores,
  consultarPorNumeroContrato,
  consultarPorCodigoEquipo,
  visualizacion,
  tienePermisoNBS
}) => {
  const [fechaActual, setFechaActual] = useState<Date>(new Date());

  useEffect(() => {
    // Actualizar fecha actual cada minuto
    const interval = setInterval(() => {
      setFechaActual(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const proyectoSeleccionado = entidadesRegistro.Proyectos.find(p => p.Id === tareaSeleccionado.ProyectoId);
  const timezoneProyecto = obtenerZonaHorariaPorPais(proyectoSeleccionado?.PaisId);
  const nombrePais = obtenerNombrePaisPorId(proyectoSeleccionado?.PaisId);

  // Actividad NBS elegida; si trae secciones (QD) se permite escoger cuáles atender
  const actividadNBSSeleccionada = entidadesRegistro.HabilitacionesNBS.find(
    h => h.TipoVisitaInstalacionId === tareaSeleccionado.TipoVisitaInstalacionId
  );
  const seccionesSoloLectura = tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
    !!visualizacion;
  const seccionesSeleccionadas = tareaSeleccionado.SeccionesChecklist ?? [];
  const seccionesPendientes = (actividadNBSSeleccionada?.Secciones ?? [])
    .filter(s => !s.Aprobada)
    .map(s => s.Numero);

  // Alto de la lista de secciones: en celular es fijo (el modal hace scroll); en computador se ajusta al espacio
  // que deja el cuerpo del modal (max 60vh) para que el modal no tenga scroll, entre 3 y 9 filas
  const contenedorModalRef = useRef<HTMLDivElement>(null);
  const cuerpoModalRef = useRef<HTMLDivElement>(null);
  const [altoListaSecciones, setAltoListaSecciones] = useState(ALTO_LISTA_SECCIONES_MOVIL);
  const hayListaSecciones = !!actividadNBSSeleccionada?.Secciones?.length;

  const ajustarAltoListaSecciones = () => {
    const contenedor = contenedorModalRef.current;
    const cuerpo = cuerpoModalRef.current;
    if (!contenedor || !cuerpo) return;
    if (window.innerWidth < ANCHO_MINIMO_ESCRITORIO) {
      setAltoListaSecciones(ALTO_LISTA_SECCIONES_MOVIL);
      return;
    }
    const altoMaximo = parseFloat(getComputedStyle(contenedor).maxHeight);
    // Alto que ocuparía todo el contenido sin el scroll del cuerpo
    const altoContenido = contenedor.offsetHeight - cuerpo.clientHeight + cuerpo.scrollHeight;
    const espacioLibre = altoMaximo - altoContenido;
    setAltoListaSecciones(actual => {
      const nuevo = Math.min(ALTO_LISTA_SECCIONES_MAX, Math.max(ALTO_LISTA_SECCIONES_MIN, Math.floor(actual + espacioLibre)));
      return Math.abs(nuevo - actual) < 1 ? actual : nuevo;
    });
  };

  // Tras cada render (cambian errores, secciones, actividad...) se recalcula; el guard evita renders en bucle
  useLayoutEffect(() => {
    if (abrir && hayListaSecciones) ajustarAltoListaSecciones();
  });

  useEffect(() => {
    if (!abrir || !hayListaSecciones) return;
    window.addEventListener("resize", ajustarAltoListaSecciones);
    return () => window.removeEventListener("resize", ajustarAltoListaSecciones);
  }, [abrir, hayListaSecciones]);

  const TecnicoSelect = (
    <Select
      label="Técnico"
      placeholder="Técnico"
      searchable
      leftSection={<IconUserCog size={15} />}
      size="xs"
      required
      readOnly={(tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma) || visualizacion}
      disabled={(tareaSeleccionado.ProyectoId === 0 && tareaSeleccionado.EquipoId === 0) || (tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion && !tareaSeleccionado.TipoVisitaInstalacionId)}
      comboboxProps={{ zIndex: 1000000 }}
      data={entidadesRegistro.Tecnicos.map(c => ({
        value: c.Id.toString(),
        label: c.NombreCompleto,
      }))}
      nothingFoundMessage="No se encontraron tecnicos"
      value={tareaSeleccionado.AsignadaA ? tareaSeleccionado.AsignadaA.toString() : null}
      clearable
      error={errores.AsignadaA}
      onChange={(value) => {
        manejarCambioValorTarea("AsignadaA", value ? Number(value) : 0)
        if (value === null) {
          agregarError('AsignadaA', 'Selecciona un técnico')
        }
        else {
          agregarError('AsignadaA', '')
        }
      }}
    />
  );

  // Con las secciones del QD las fechas pasan a una fila propia bajo la tarjeta y el mapa, alineadas entre sí
  const fechasEnFilaPropia = tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion && (actividadNBSSeleccionada?.Secciones?.length ?? 0) > 0;
  const CamposFechas = (
    <>
      <Grid.Col span={fechasEnFilaPropia ? { base: 12, md: 6 } : 12}>
        <DateTimePicker
          leftSection={<IconCalendarTime size={15} />}
          label="Fecha Inicio Proyectada"
          description={tareaSeleccionado.ProyectoId !== 0 ? `Validando según hora de ${nombrePais}` : undefined}
          placeholder="Fecha Inicio Proyectada"
          size="xs"
          required
          readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
            tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
            tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
            tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido || visualizacion}
          value={tareaSeleccionado.FechaInicio ? new Date(tareaSeleccionado.FechaInicio) : null}
          defaultDate={tareaSeleccionado.FechaInicio ? new Date(tareaSeleccionado.FechaInicio) : undefined}
          popoverProps={{ zIndex: 1000000 }}
          excludeDate={(date) => {
            const ahora = dayjs(fechaActual).tz(timezoneProyecto).startOf('day');
            const esFechaDeTarea = tareaSeleccionado.FechaInicio
              ? dayjs(date).isSame(dayjs(tareaSeleccionado.FechaInicio), 'day')
              : false;
            // Bloquear si es antes de hoy Y no es la fecha que ya tiene la tarea
            return dayjs(date).isBefore(ahora, 'day') && !esFechaDeTarea;
          }}
          error={errores.FechaInicio}
          onChange={(value) => {
            if (!value) {
              manejarCambioValorTarea("FechaInicio", "");
              manejarCambioValorTarea("FechaFin", "");
              return;
            }
          
            // Usar la hora del proyecto localmente
            const fechaSeleccionada = dayjs(value);
          
            const ahoraProyectoString = dayjs().tz(timezoneProyecto).format("YYYY-MM-DDTHH:mm:ss");
            const ahoraProyecto = dayjs(ahoraProyectoString);
            // Margen de 2 minutos para evitar falsos positivos por diferencias de reloj/zona horaria
            const ahoraConMargen = ahoraProyecto.subtract(2, "minute");
          
            const esHoy = fechaSeleccionada.isSame(ahoraProyecto, "day");
          
            const esCreacion = !tareaSeleccionado.Id;
          
            if (esCreacion && fechaSeleccionada.isBefore(ahoraProyecto, "day")) {
              agregarError("FechaInicio", `La fecha de inicio debe ser mayor o igual a la fecha actual en ${nombrePais}`);
              return;
            }
          
            else if (esCreacion && esHoy && fechaSeleccionada.isBefore(ahoraConMargen, "minute")) {
              agregarError("FechaInicio", `La hora de inicio no puede ser anterior a la hora actual en ${nombrePais}`);
              return;
            }
          
            else agregarError("FechaInicio", "");
          
            const fechaInicio = fechaSeleccionada.format("YYYY-MM-DDTHH:mm:ss");
            manejarCambioValorTarea("FechaInicio", fechaInicio);
          
            // Recalcular Fecha Fin
            if (tareaSeleccionado.Duracion) {
              const fechaFin = fechaSeleccionada
                .add(Number(tareaSeleccionado.Duracion), "minutes")
                .format("YYYY-MM-DDTHH:mm:ss");
          
              manejarCambioValorTarea("FechaFin", fechaFin);
            } else {
              manejarCambioValorTarea("FechaFin", "");
            }
          }}
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: false },
            format: '12h',
          }}
        />
      </Grid.Col>
      <Grid.Col span={fechasEnFilaPropia ? { base: 12, md: 3 } : { base: 12, md: 6 }}>
        <NumberInput
          label="Duración (Minutos)"
          size="xs"
          min={0}
          maxLength={4}
          readOnly={(tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
            tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
            tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
            tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido) || visualizacion}
          leftSection={<IconClockHour1 size={17} />}
          styles={{
            label: { opacity: 1, color: "black", fontWeight: 500 } // 👀 forzamos visibilidad
          }}
          value={tareaSeleccionado.Duracion}
          onChange={(value) => {
            manejarCambioValorTarea("Duracion", value ?? 0);
          
            if (!value) {
              agregarError("Duracion", "Este campo es requerido");
              manejarCambioValorTarea("FechaFin", "");
              return;
            }
          
            agregarError("Duracion", "");
          
            if (tareaSeleccionado.FechaInicio) {
              const fechaFin = dayjs(tareaSeleccionado.FechaInicio)
                .add(Number(value), "minutes")
                .format("YYYY-MM-DDTHH:mm:ss");
          
              manejarCambioValorTarea("FechaFin", fechaFin);
            }
          }}
          error={errores.Duracion}
          required
        />
      </Grid.Col>
      <Grid.Col span={fechasEnFilaPropia ? { base: 12, md: 3 } : { base: 12, md: 6 }}>
        <DateTimePicker
          leftSection={<IconCalendarTime size={15} />}
          label="Fecha Fin Proyectada"
          placeholder="Fecha Fin Proyectada"
          size="xs"
          required
          value={tareaSeleccionado.FechaFin}
          popoverProps={{ zIndex: 1000000 }}
          excludeDate={(date) => {
            const ahora = dayjs(fechaActual).tz(timezoneProyecto).startOf('day');
            const esFechaDeTarea = tareaSeleccionado.FechaFin
              ? dayjs(date).isSame(dayjs(tareaSeleccionado.FechaFin), 'day')
              : false;
            return dayjs(date).isBefore(ahora, 'day') && !esFechaDeTarea;
          }}
          error={errores.FechaFin}
          readOnly
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: false },
            format: '12h',
          }}
        />
      </Grid.Col>
    </>
  );

  return (
    <Modal
      closeOnClickOutside={false}
      opened={abrir}
      styles={{
        header: { backgroundColor: '#0074d914' }
      }}
      onClose={() => abrirModalTarea(false, crearEstadoInicialTarea())}
      title={<Text fw={500} fz="lg">
        {tareaSeleccionado.Id && visualizacion ? `Ver Tarea #${tareaSeleccionado.Id} - ${tareaSeleccionado.EstadoServicio?.Nombre}` : tareaSeleccionado.Id && !visualizacion ? `Editar Tarea #${tareaSeleccionado.Id} - ${tareaSeleccionado.EstadoServicio?.Nombre}` : "Agregar Tarea"}
      </Text>}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 1,
        zIndex: 100000,
      }}
      zIndex={100000}
      centered
      size={"90%"}
      radius="md"
      withCloseButton
    >
      <Box
        ref={contenedorModalRef}
        style={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "60vh",
        }}>
        <Box
          ref={cuerpoModalRef}
          p="md"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: 'hidden',
          }}
        >
          <Grid gutter="sm" columns={15} grow={!tienePermisoNBS}>
            <Grid.Col span={{ base: 15, md: 3 }}>
              <Select
                leftSection={<IconFileText size={15} />}
                label="Proyecto"
                placeholder="Proyecto"
                searchable
                size="xs"
                required
                comboboxProps={{ zIndex: 1000000 }}
                data={entidadesRegistro.Proyectos.map(c => ({
                  value: c.Id.toString(),
                  label: c.Nombre,
                }))}
                nothingFoundMessage="No se encontraron proyectos"
                value={tareaSeleccionado.ProyectoId ? tareaSeleccionado.ProyectoId.toString() : null}
                clearable
                readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                  visualizacion}
                error={errores.ProyectoId}
                onChange={(value) => {
                  manejarCambioValorTarea("ProyectoId", value ? Number(value) : 0)
                  if (value === null) {
                    agregarError('ProyectoId', 'Selecciona un proyecto')
                  }
                  else {
                    agregarError('ProyectoId', '')
                    agregarError('ClienteId', '')
                    agregarError('NumeroContrato', '')
                    agregarError('EquipoId', '')
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 15, md: 3 }}>
              <Select
                leftSection={<IconUserCheck size={15} />}
                label="Cliente"
                placeholder="Cliente"
                searchable
                size="xs"
                required
                readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                  visualizacion}
                comboboxProps={{ zIndex: 1000000 }}
                data={entidadesRegistro.Clientes.map(c => ({
                  value: c.Id.toString(),
                  label: c.Nombre,
                }))}
                nothingFoundMessage="No se encontraron clientes"
                value={tareaSeleccionado.ClienteId ? tareaSeleccionado.ClienteId.toString() : null}
                clearable
                error={errores.ClienteId}
                onChange={(value) => {
                  manejarCambioValorTarea("ClienteId", value ? Number(value) : 0)
                  if (value === null) {
                    agregarError('ClienteId', 'Selecciona un cliente')
                  }
                  else {
                    agregarError('ClienteId', '')
                    agregarError('ProyectoId', '')
                    agregarError('NumeroContrato', '')
                    agregarError('EquipoId', '')
                  }
                }}
              />
            </Grid.Col>
            {entidadesRegistro.Equipos.length > 1 ? (
              <Grid.Col span={{ base: 15, md: 3 }}>
                <Select
                  label="Código Equipo"
                  placeholder="Código Equipo"
                  leftSection={<IconSettingsUp size={15} />}
                  searchable
                  size="xs"
                  required
                  comboboxProps={{ zIndex: 1000000 }}
                  data={entidadesRegistro.Equipos.map(c => ({
                    value: c.Id.toString(),
                    label: `${c.Codigo} - ${c.Nombre}`
                  }))}
                  nothingFoundMessage="No se encontraron equipos"
                  value={tareaSeleccionado.EquipoId ? tareaSeleccionado.EquipoId.toString() : null}
                  clearable
                  readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                    visualizacion}
                  error={errores.EquipoId}
                  onChange={(value) => {
                    const equipoId = value ? parseInt(value) : 0;
                    manejarCambioValorTarea("EquipoId", equipoId);

                    if (!value) {
                      agregarError('EquipoId', 'Selecciona un equipo');
                    } else {
                      agregarError('EquipoId', '');
                    }
                  }}
                />
              </Grid.Col>
            ) : (
              <Grid.Col span={{ base: 15, md: 3 }}>
                <TextInput
                  label="Código Equipo"
                  placeholder="Código Equipo"
                  size="xs"
                  rightSection={tareaSeleccionado.CodigoEquipo !== '' && !visualizacion ? <IconSearch cursor='pointer' onClick={() => consultarPorCodigoEquipo(tareaSeleccionado.CodigoEquipo)} size={14} /> : null}
                  maxLength={20}
                  readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                    visualizacion}
                  value={tareaSeleccionado.CodigoEquipo}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    const regex = /^[A-Za-z0-9-]*$/;
                    if (regex.test(value)) {
                      manejarCambioValorTarea("CodigoEquipo", value);
                    }

                    if (!value) {
                      agregarError("EquipoId", "Este campo es requerido");
                      manejarCambioValorTarea("EquipoId", 0);
                    } else {
                      agregarError("EquipoId", "");
                      manejarCambioValorTarea("EquipoId", 0);
                    }
                  }}
                  description={tareaSeleccionado && tareaSeleccionado.Equipo && tareaSeleccionado.EquipoId ? tareaSeleccionado.Equipo.Nombre : ''}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  error={errores.EquipoId}
                  required
                />
              </Grid.Col>
            )}

            <Grid.Col span={{ base: 15, md: 3 }}>
              <TextInput
                label="Número Contrato"
                placeholder="Número Contrato"
                size="xs"
                rightSection={tareaSeleccionado.NumeroContrato !== '' && !
                  visualizacion ? <IconSearch cursor='pointer' onClick={() => consultarPorNumeroContrato(tareaSeleccionado.NumeroContrato)} size={14} /> : null}
                maxLength={20}
                value={tareaSeleccionado.NumeroContrato}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  const regex = /^[0-9.-]*$/;
                  if (regex.test(value)) {
                    manejarCambioValorTarea("NumeroContrato", value);
                  }

                  if (!value) {
                    agregarError("NumeroContrato", "Este campo es requerido");
                  } else {
                    agregarError("NumeroContrato", "");
                    agregarError("ProyectoId", "");
                    agregarError("ClienteId", "");
                  }
                }}
                readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                  tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                  visualizacion}
                error={errores.NumeroContrato}
                required
              />
            </Grid.Col>

            {/* Tipo Tarea: al final de la primera fila cuando el usuario tiene permiso NBS, ya que sus opciones dependen del proyecto/contrato seleccionado */}
            {tienePermisoNBS && (
              <Grid.Col span={{ base: 15, md: 3 }}>
                <Select
                  label="Tipo Tarea"
                  placeholder="Tipo Tarea"
                  searchable
                  size="xs"
                  required
                  comboboxProps={{ zIndex: 1000000 }}
                  data={entidadesRegistro.TiposTarea.map(c => ({
                    value: c.Id.toString(),
                    label: c.Nombre,
                  }))}
                  nothingFoundMessage="No se encontraron tipos de tareas"
                  value={tareaSeleccionado.TipoServicioId ? tareaSeleccionado.TipoServicioId.toString() : null}
                  clearable={entidadesRegistro.TiposTarea.length > 1}
                  // Volver a elegir la opción seleccionada no la deselecciona: el campo es obligatorio
                  allowDeselect={false}
                  // Con un único tipo posible (ej. Planeador: Visita Instalación) se autoselecciona y no se puede cambiar
                  readOnly={entidadesRegistro.TiposTarea.length === 1 ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                    tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                    visualizacion}
                  error={errores.TipoServicioId}
                  onChange={(value) => {
                    manejarCambioValorTarea("TipoServicioId", value ? Number(value) : 0)
                    if (value === null) {
                      agregarError('TipoServicioId', 'Selecciona un tipo')
                    }
                    else {
                      agregarError('TipoServicioId', '')
                    }
                  }}
                />
              </Grid.Col>
            )}
          </Grid>
          
          <Grid gutter="sm" mt="sm">
            {/* Sin permiso NBS: Tipo Tarea y Técnico en fila completa (ancho total), uno al lado del otro */}
            {!tienePermisoNBS && tareaSeleccionado.TipoServicioId !== EnumTipoTarea.VisitaInstalacion && (
              <>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Select
                    label="Tipo Tarea"
                    placeholder="Tipo Tarea"
                    searchable
                    size="xs"
                    required
                    comboboxProps={{ zIndex: 1000000 }}
                    data={entidadesRegistro.TiposTarea.map(c => ({
                      value: c.Id.toString(),
                      label: c.Nombre,
                    }))}
                    nothingFoundMessage="No se encontraron tipos de tareas"
                    value={tareaSeleccionado.TipoServicioId ? tareaSeleccionado.TipoServicioId.toString() : null}
                    clearable={entidadesRegistro.TiposTarea.length > 1}
                    allowDeselect={false}
                    readOnly={entidadesRegistro.TiposTarea.length === 1 ||
                      tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                      tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                      tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                      tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                      tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                      visualizacion}
                    error={errores.TipoServicioId}
                    onChange={(value) => {
                      manejarCambioValorTarea("TipoServicioId", value ? Number(value) : 0)
                      if (value === null) {
                        agregarError('TipoServicioId', 'Selecciona un tipo')
                      } else {
                        agregarError('TipoServicioId', '')
                      }
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  {TecnicoSelect}
                </Grid.Col>
              </>
            )}

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Grid gutter="sm">
                {tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion ? (
                  <Grid.Col span={12}>
                    <Select
                      label="Actividad a programar"
                      placeholder="Selecciona actividad NBS"
                      size="xs"
                      searchable
                      required
                      comboboxProps={{ zIndex: 1000000 }}
                      data={entidadesRegistro.HabilitacionesNBS
                        .map(h => ({
                          value: h.TipoVisitaInstalacionId.toString(),
                          label: h.Nombre,
                        }))}
                      disabled={tareaSeleccionado.EquipoId === 0}
                      nothingFoundMessage={tareaSeleccionado.EquipoId === 0 ? "Selecciona un Equipo para cargar actividades" : "No hay actividades NBS para este equipo"}
                      value={tareaSeleccionado.TipoVisitaInstalacionId ? tareaSeleccionado.TipoVisitaInstalacionId.toString() : null}
                      clearable
                      readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma ||
                        visualizacion}
                      error={errores.TipoVisitaInstalacionId}
                      onChange={(value) => {
                        manejarCambioValorTarea("TipoVisitaInstalacionId", value ? Number(value) : null)
                        manejarCambioValorTarea("AsignadaA", 0);
                        if (value === null && tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion) {
                          agregarError('TipoVisitaInstalacionId', 'Selecciona una actividad NBS')
                        } else {
                          agregarError('TipoVisitaInstalacionId', '')
                        }
                      }}
                    />
                    {actividadNBSSeleccionada && actividadNBSSeleccionada.Secciones?.length > 0 && (
                      <Box mt="sm">
                        <Group justify="space-between" mb={4}>
                          <Text size="xs" fw={500}>Secciones a atender</Text>
                          <Text size="xs" c="dimmed">
                            {seccionesSeleccionadas.length > 0
                              ? `${seccionesSeleccionadas.length} de ${actividadNBSSeleccionada.Secciones.length}`
                              : "Todas"}
                          </Text>
                        </Group>
                        <Paper withBorder radius="sm">
                          {!seccionesSoloLectura && (
                            <Group gap={4} px="xs" py={4} style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
                              <Button
                                variant="light"
                                size="compact-xs"
                                disabled={seccionesPendientes.length === 0}
                                onClick={() => manejarCambioValorTarea("SeccionesChecklist", seccionesPendientes)}
                              >
                                Seleccionar pendientes
                              </Button>
                              <Button
                                variant="subtle"
                                // red.8 explícito: con primaryShade 0 del tema, "red" resuelve a un tono casi blanco
                                color="red.8"
                                size="compact-xs"
                                disabled={seccionesSeleccionadas.length === 0}
                                onClick={() => manejarCambioValorTarea("SeccionesChecklist", [])}
                              >
                                Limpiar
                              </Button>
                            </Group>
                          )}
                          <ScrollArea h={altoListaSecciones} type="auto" offsetScrollbars>
                            <Checkbox.Group
                              value={seccionesSeleccionadas.map(String)}
                              onChange={(valores) =>
                                manejarCambioValorTarea(
                                  "SeccionesChecklist",
                                  valores.map(Number).sort((a, b) => a - b)
                                )
                              }
                            >
                              {actividadNBSSeleccionada.Secciones.map((seccion, indice) => (
                                <Group
                                  key={seccion.Numero}
                                  justify="space-between"
                                  wrap="nowrap"
                                  gap="xs"
                                  px="sm"
                                  py={6}
                                  style={indice > 0 ? { borderTop: "1px solid var(--mantine-color-default-border)" } : undefined}
                                >
                                  <Checkbox
                                    size="xs"
                                    value={seccion.Numero.toString()}
                                    disabled={seccionesSoloLectura}
                                    style={{ flex: 1 }}
                                    label={
                                      <Text size="xs">
                                        <Text span size="xs" fw={600} c="dimmed" mr={6}>{seccion.Numero}</Text>
                                        {seccion.Nombre}
                                      </Text>
                                    }
                                  />
                                  {seccion.Aprobada && (
                                    <Group gap={4} wrap="nowrap">
                                      <IconCircleCheckFilled size={14} color="var(--mantine-color-green-6)" />
                                      <Text size="xs" fw={500} c="green.7">Aprobada</Text>
                                    </Group>
                                  )}
                                </Group>
                              ))}
                            </Checkbox.Group>
                          </ScrollArea>
                        </Paper>
                        <Text size="xs" c="dimmed" mt={4}>Sin selección se atienden todas las secciones</Text>
                      </Box>
                    )}
                  </Grid.Col>
                 ) : tienePermisoNBS ? (
                  /* Con permiso NBS: Técnico en columna izquierda */
                  <Grid.Col span={12}>
                    {TecnicoSelect}
                  </Grid.Col>
                ) : null}

                {!fechasEnFilaPropia && CamposFechas}

                {tareaSeleccionado.TipoServicioId === EnumTipoTarea.Emergencia && (
                  <Grid.Col span={12}>
                    <Select
                      label="Motivo Emergencia"
                      placeholder="Motivo Emergencia"
                      searchable
                      leftSection={<IconMessageExclamation size={15} />}
                      size="xs"
                      required
                      comboboxProps={{ zIndex: 1000000 }}
                      data={entidadesRegistro.CausalesEmergencia.map(c => ({
                        value: c.Id.toString(),
                        label: c.Nombre,
                      }))}
                      readOnly={tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Exitoso ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.EnCurso ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Cancelado ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.Fallido ||
                        tareaSeleccionado.EstadoTareaId === EnumEstadoTarea.PendienteFirma || visualizacion}
                      nothingFoundMessage="No se encontraron motivos"
                      value={tareaSeleccionado.CausalEmergenciaId ? tareaSeleccionado.CausalEmergenciaId.toString() : null}
                      clearable
                      error={errores.CausalEmergenciaId}
                      onChange={(value) => {
                        manejarCambioValorTarea("CausalEmergenciaId", value ? Number(value) : 0)
                        if (value === null && tareaSeleccionado.TipoServicioId === EnumTipoTarea.Emergencia) {
                          agregarError('CausalEmergenciaId', 'Selecciona una causal')
                        }
                        else {
                          agregarError('CausalEmergenciaId', '')
                        }
                      }}
                    />
                  </Grid.Col>
                )}

                {tareaSeleccionado.TipoServicioId !== EnumTipoTarea.VisitaInstalacion && (
                  <Grid.Col span={12}>
                    <MultiSelect
                      label="Modulos"
                      placeholder="Modulos"
                      name="ModulosChecklist"
                      required
                      data={entidadesRegistro.ModulosCheckList.map(mch => ({
                        value: mch.Id.toString(),
                        label: mch.Nombre,
                      }))}
                      disabled={
                        tareaSeleccionado.TipoServicioId === null || 
                        tareaSeleccionado.TipoServicioId === undefined || 
                        tareaSeleccionado.TipoServicioId === 0 ||
                        (tareaSeleccionado.TipoServicioId === EnumTipoTarea.Preventivo && tareaSeleccionado.EquipoId === 0)
                      }
                      error={errores.ModulosChecklist}
                      readOnly={tareaSeleccionado.TipoServicioId === EnumTipoTarea.Emergencia || tareaSeleccionado.TipoServicioId === EnumTipoTarea.Correctivo || tareaSeleccionado.TipoServicioId === EnumTipoTarea.Acompanamiento || visualizacion}
                      comboboxProps={{ zIndex: 1000000 }}
                      value={tareaSeleccionado.ModulosChecklist ? tareaSeleccionado.ModulosChecklist.map(t => t.toString()) : []}
                      nothingFoundMessage="No se encontraron modulos"
                      size="xs"
                      onChange={(valores) => {
                        manejarCambioValorTarea('ModulosChecklist', valores.map(v => Number(v)));

                        if (valores.length === 0 && tareaSeleccionado.TipoServicioId !== EnumTipoTarea.Emergencia && tareaSeleccionado.TipoServicioId !== EnumTipoTarea.Correctivo && tareaSeleccionado.TipoServicioId !== EnumTipoTarea.Acompanamiento) {
                          agregarError('ModulosChecklist', 'Selecciona minimo un modulo')
                        }
                        else {
                          agregarError('ModulosChecklist', '')
                        }
                      }}
                      leftSection={<IconChecklist size={17} />}
                      searchable
                    />
                  </Grid.Col>
                )}
              </Grid>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex', flexDirection: 'column' }}>
              {tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion && (
                <Grid gutter="sm">
                  <Grid.Col span={12}>
                    {TecnicoSelect}
                  </Grid.Col>
                </Grid>
              )}
              <Box style={{ 
                zIndex: 10, 
                display: 'flex', 
                flexDirection: 'column', 
                flex: 1,
                position: 'relative',
                // Con secciones el mapa se estira al alto de la tarjeta de secciones
                minHeight: fechasEnFilaPropia ? '180px' : undefined,
                marginTop:tareaSeleccionado.TipoServicioId !== EnumTipoTarea.VisitaInstalacion ? '24px' : '12px'
              }}>
                <Box style={{
                  position: tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion && !fechasEnFilaPropia ? 'static' : 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  height: tareaSeleccionado.TipoServicioId === EnumTipoTarea.VisitaInstalacion && !fechasEnFilaPropia ? '180px' : '100%'
                }}>
                  <MapaVista
                    latitud={Number(entidadesRegistro.Proyectos.find(p => p.Id === tareaSeleccionado.ProyectoId)?.Latitud)}
                    longitud={Number(entidadesRegistro.Proyectos.find(p => p.Id === tareaSeleccionado.ProyectoId)?.Longitud)}
                    popupText={entidadesRegistro.Proyectos.find(p => p.Id === tareaSeleccionado.ProyectoId)?.Direccion || ''}
                    height="100%"
                  />
                </Box>
              </Box>
            </Grid.Col>
          </Grid>
          {fechasEnFilaPropia && (
            <Grid gutter="sm" mt="sm" align="flex-end">
              {CamposFechas}
            </Grid>
          )}
        </Box>
        <Box
          style={{
            backgroundColor: "#fff",
          }}
        >
          <Group justify="flex-end">
            <Button
              onClick={guardarEditarTarea}
              color="primary"
              variant="filled"
              size='compact-sm'
              radius="lg"
              disabled={visualizacion}
              rightSection={tareaSeleccionado.Id ? <IconPencilCheck opacity={100} color="white" size={15} /> : <IconDeviceFloppy opacity={100} color="white" size={15} />}
            >
              <Text fw={500} fz={11}>{tareaSeleccionado.Id ? "Editar Tarea" : "Guardar Tarea"}</Text>
            </Button>


          </Group>
        </Box>
      </Box>

    </Modal >
  );
};

export default ModalTareaVista;
